import { isBuildHost } from "@/lib/isBuildHost";
import { BuildHostFallback } from "@/components/build/BuildHostFallback";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import BuildLogin from "@/pages/BuildLogin";
import BuildConfirmSignIn from "@/pages/BuildConfirmSignIn";
import LegalNotice from "@/pages/LegalNotice";
import BuildApplyV2 from "@/pages/buildApplyV2/BuildApplyV2";
import BuildApplyRouter from "@/pages/BuildApplyRouter";
import Dashboard from "@/pages/Dashboard";
import { ClientsList, ClientDetail } from "@/pages/Clients";
import { Contacts, Projects } from "@/pages/Records";
import Search from "@/pages/Search";
import Admin from "@/pages/Admin";
import SettingsPage from "@/pages/Settings";
import Knowledge from "@/pages/Knowledge";
import Meetings from "@/pages/Meetings";
import EmailGenerator from "@/pages/EmailGenerator";
import ProjectDashboard from "@/pages/ProjectDashboard";
import ContactDetail from "@/pages/ContactDetail";
import Finance from "@/pages/Finance";
import Tasks from "@/pages/Tasks";
import CommercialDocs from "@/pages/CommercialDocs";
import AiAssistant from "@/pages/AiAssistant";
import { BuildDirectory, FreelancerProfile } from "@/pages/Build";
import VideoLibrary from "@/pages/VideoLibrary";
import ApplicationsQueue from "@/pages/ApplicationsQueue";
import AdminOpportunities from "@/pages/AdminOpportunities";
import InterviewQueue from "@/pages/InterviewQueue";
import PastInterviews from "@/pages/PastInterviews";
import Community from "@/pages/Community";
import Gallery from "@/pages/Gallery";
import NotFound from "@/pages/NotFound";
import { Logo } from "@/components/Logo";
import ClientPortalLogin from "@/pages/clientPortal/ClientPortalLogin";
import ClientPortalChooseCompany from "@/pages/clientPortal/ClientPortalChooseCompany";
import ClientPortalHome from "@/pages/clientPortal/ClientPortalHome";
import ClientPortalProjectsList from "@/pages/clientPortal/ClientPortalProjectsList";
import ClientPortalProject from "@/pages/clientPortal/ClientPortalProject";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import StaffSetPassword from "@/pages/StaffSetPassword";
import ClientRequests from "@/pages/ClientRequests";
import Ledger from "@/pages/Ledger";
import CreativeRadar from "@/pages/CreativeRadar";
import PartnerWorkspaceLogin from "@/pages/partnerWorkspace/PartnerWorkspaceLogin";
import PartnerWorkspaceConfirmSignIn from "@/pages/partnerWorkspace/PartnerWorkspaceConfirmSignIn";
import PartnerWorkspaceHome from "@/pages/partnerWorkspace/PartnerWorkspaceHome";
import PartnerWorkspacePlaceholder from "@/pages/partnerWorkspace/PartnerWorkspacePlaceholder";
import PartnerWorkspaceRateCard from "@/pages/partnerWorkspace/PartnerWorkspaceRateCard";
import PartnerWorkspaceDiscover from "@/pages/partnerWorkspace/PartnerWorkspaceDiscover";
import AgencyAuth from "@/pages/agency/AgencyAuth";
import AgencyHome from "@/pages/agency/AgencyHome";
import AgencyProjects from "@/pages/agency/AgencyProjects";
import AgencyProjectDetail from "@/pages/agency/AgencyProjectDetail";
import AgencyDiscover from "@/pages/agency/AgencyDiscover";
import AgencyArchives from "@/pages/agency/AgencyArchives";
import AgencyDocuments from "@/pages/agency/AgencyDocuments";
import AgencyProfile from "@/pages/agency/AgencyProfile";
import AgencyEstimator from "@/pages/agency/AgencyEstimator";
import MyQuoteRequests from "@/pages/agency/MyQuoteRequests";
import QuoteRequestDetail from "@/pages/agency/QuoteRequestDetail";
import AgencyQuoteRequests from "@/pages/AgencyQuoteRequests";
import AdminPartnerRateCards from "@/pages/AdminPartnerRateCards";
import AdminEmailTemplates from "@/pages/AdminEmailTemplates";
import Partners from "@/pages/Partners";
import PartnerDetail from "@/pages/PartnerDetail";
import CollaborationDeepLink from "@/pages/CollaborationDeepLink";
import BuildOpportunityDeepLink from "@/pages/BuildOpportunityDeepLink";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { BuildAppMetadata } from "@/components/build/BuildAppMetadata";

import { computeProtectedRedirect } from "@/auth/computeProtectedRedirect";

// Phase-1 perf batch Wave B, Item 8 (2026-09): the 3 largest, least-frequently-opened staff
// pages route-split out of the main bundle. Route/auth guards around them (Protected staffOnly
// AppShell) are completely untouched — only the component's OWN bundle chunk is deferred.
const CommercialQuoteBuilder = lazy(() => import("@/pages/CommercialQuoteBuilder"));
const PricingManager = lazy(() => import("@/pages/PricingManager"));
const FreelancerManagement = lazy(() => import("@/pages/FreelancerManagement"));
const Reporting = lazy(() => import("@/pages/Reporting"));

// Cursor Ticket 010: same lazy()/import() pattern for the 5 portal shells (6 components —
// both Production Partner shells are still live). Named-export shells unwrap to the default
// shape React.lazy requires; no shell file, guard, or data-fetch logic is changed.
const AppShell = lazy(() => import("@/layouts/AppShell").then((m) => ({ default: m.AppShell })));
const ClientPortalShell = lazy(() => import("@/pages/clientPortal/ClientPortalShell").then((m) => ({ default: m.ClientPortalShell })));
const AgencyShell = lazy(() => import("@/pages/agency/AgencyShell").then((m) => ({ default: m.AgencyShell })));
const PartnerWorkspaceShell = lazy(() => import("@/pages/partnerWorkspace/PartnerWorkspaceShell").then((m) => ({ default: m.PartnerWorkspaceShell })));
const FreelancerPortal = lazy(() => import("@/pages/FreelancerPortal"));
const PartnerPortalLayout = lazy(() => import("@/pages/PartnerPortal").then((m) => ({ default: m.PartnerPortalLayout })));
// Same module as PartnerPortalLayout — must also be dynamic or webpack keeps the whole file
// (and its MemberCard import of FreelancerPortal) in the main bundle. Route components only;
// PartnerPortal.jsx internals are untouched.
const PartnerProjectsList = lazy(() => import("@/pages/PartnerPortal").then((m) => ({ default: m.PartnerProjectsList })));
const PartnerProjectDetail = lazy(() => import("@/pages/PartnerPortal").then((m) => ({ default: m.PartnerProjectDetail })));
const PartnerDiscoverTalent = lazy(() => import("@/pages/PartnerPortal").then((m) => ({ default: m.PartnerDiscoverTalent })));
// PublicMemberProfile statically imports MemberCard from FreelancerPortal.jsx. Leaving this
// eager would pull the Freelancer Portal back into every other portal's initial payload.
const PublicMemberProfile = lazy(() => import("@/pages/PublicMemberProfile"));
// Wave R2 (2026-10) — standalone, nav-less mobile quick-upload route ("staff on location").
// Deliberately NOT nested inside the AppShell Route group below (no sidebar) but still behind
// the exact same <Protected staffOnly> gate every other staff route uses — never a public or
// token link. The finer client_portal.admin check happens inside the page itself.
const RecceMobileUpload = lazy(() => import("@/pages/RecceMobileUpload"));
// Phase 4 (2026-10, Design System 2.0 visual reskin) — Recce's full-screen mode, same
// lazy-route pattern as RecceMobileUpload above (standalone, outside the AppShell group).
const ProjectRecceFullScreen = lazy(() => import("@/pages/ProjectRecceFullScreen"));

function PageLoadingFallback() {
  return (
    <div className="flex h-[60vh] items-center justify-center" data-testid="page-loading-fallback">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#E7E5E4] border-t-[#FFC817]" />
    </div>
  );
}

function Protected({ children, staffOnly, buildLogin, clientPortal, partnerPortal, agencyPortal, communityMember }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-[#1C1C1E]">
      <Logo theme="dark" size="lg" />
    </div>
  );
  if (!user) {
    const loginPath = clientPortal ? "/client-portal/login" : partnerPortal ? "/partner-portal/login" : agencyPortal ? "/agency/login" : buildLogin ? "/build/login" : "/login";
    // Wave 3 (2026-09): capture the destination the visitor actually wanted (e.g. a shared
    // Collaboration deep link) so BuildLogin.jsx can send them there after signing in, instead
    // of always landing on the generic /build/me. Scoped to the buildLogin login path only —
    // staff/client/partner/agency logins are unaffected (no ?next= is ever appended for them).
    const dest = buildLogin ? `${loginPath}?next=${encodeURIComponent(location.pathname + location.search)}` : loginPath;
    return <Navigate to={dest} replace />;
  }
  // 2026-09 security fix (Bavna #24): the full role-exclusion table now lives in
  // computeProtectedRedirect.js as a pure, unit-tested function — see its own test file for
  // the permanent regression coverage (a missing exclusion here is exactly how role=
  // partner_agency slipped through staffOnly before this fix).
  const redirect = computeProtectedRedirect(user, { staffOnly, clientPortal, partnerPortal, agencyPortal, communityMember });
  if (redirect) return <Navigate to={redirect} replace />;
  return children;
}

function App() {
  return (
    // Part 6 hardening (2026-10): a single app-wide boundary, mounted OUTSIDE every lazy
    // shell/Suspense below — the last line of defense for an error thrown by AuthProvider,
    // the router's own glue, or any future page that isn't yet wrapped by its own portal-level
    // boundary. Not keyed by route (there's no Router context yet at this level to read a
    // pathname from) — a crash here is rare enough that "Reload" is an acceptable recovery.
    <RouteErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="bottom-right" />
          {IS_BUILD_HOST ? <BuildHostRoutes /> : <PlatformRoutes />}
        </BrowserRouter>
      </AuthProvider>
    </RouteErrorBoundary>
  );
}

// build.njmpro.com custom domain (2026-09): the freelancer "NJM Build" portal served at CLEAN root
// URLs (build.njmpro.com/login, /apply, /me, …) — the staff app + all other portals stay on
// platform.njmpro.com. Host-gated, so this whole branch is DORMANT on platform.njmpro.com and the
// preview domain (zero risk) and activates the instant build.njmpro.com resolves to this deployment.
const IS_BUILD_HOST = typeof window !== "undefined" && isBuildHost(window.location.hostname);

// On the build host, transparently strip any stray "/build" prefix so every existing internal link
// and every Protected redirect (which all point at "/build/…") resolves to the clean route tree
// below — WITHOUT touching a single freelancer component. Staff-only build paths never render here.
function BuildHostRewrite() {
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    if (loc.pathname === "/build" || loc.pathname.startsWith("/build/")) {
      const clean = loc.pathname.replace(/^\/build/, "") || "/login";
      nav(clean + loc.search + loc.hash, { replace: true });
    }
  }, [loc, nav]);
  return null;
}

function BuildHostRoutes() {
  return (
    <>
      <BuildAppMetadata />
      <BuildHostRewrite />
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/login" element={<BuildLogin />} />
          <Route path="/confirm-signin" element={<BuildConfirmSignIn />} />
          <Route path="/apply" element={<BuildApplyRouter />} />
          <Route path="/apply/v2" element={<BuildApplyV2 />} />
          <Route path="/me" element={<Protected buildLogin><FreelancerPortal /></Protected>} />
          <Route path="/member/:id" element={<Protected communityMember buildLogin><RouteErrorBoundary homePath="/" homeLabel="Go to homepage"><PublicMemberProfile /></RouteErrorBoundary></Protected>} />
          <Route path="/community/collaboration/:id" element={<Protected communityMember buildLogin><RouteErrorBoundary homePath="/" homeLabel="Go to homepage"><CollaborationDeepLink /></RouteErrorBoundary></Protected>} />
          <Route path="/opportunities/:id" element={<Protected buildLogin><RouteErrorBoundary homePath="/" homeLabel="Go to homepage"><BuildOpportunityDeepLink /></RouteErrorBoundary></Protected>} />
          <Route path="/legal" element={<LegalNotice />} />
          <Route path="*" element={<BuildHostFallback />} />
        </Routes>
      </Suspense>
    </>
  );
}

// On platform.njmpro.com, once build.njmpro.com is confirmed LIVE (flag flipped via redeploy),
// bounce the FREELANCER-facing /build/* paths to the new domain so previously-shared community
// links keep working. Staff paths (/build directory, /build/f/:id) are NEVER redirected.
function PlatformBuildRedirect() {
  const loc = useLocation();
  useEffect(() => {
    if (process.env.REACT_APP_BUILD_DOMAIN_LIVE !== "true") return;
    const p = loc.pathname;
    if (p.startsWith("/build/") && !p.startsWith("/build/f/")) {
      const clean = p.replace(/^\/build/, "") || "/login";
      window.location.replace(`https://build.njmpro.com${clean}${loc.search}${loc.hash}`);
    }
  }, [loc]);
  return null;
}

function PlatformRoutes() {
  return (
    <>
      <PlatformBuildRedirect />
      <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/staff/set-password" element={<StaffSetPassword />} />
          <Route path="/build/login" element={<BuildLogin />} />
          <Route path="/build/confirm-signin" element={<BuildConfirmSignIn />} />
          <Route path="/legal" element={<LegalNotice />} />
          <Route path="/build/apply" element={<BuildApplyRouter />} />
          <Route path="/build/apply/v2" element={<BuildApplyV2 />} />
          <Route path="/build/me" element={<Protected buildLogin><FreelancerPortal /></Protected>} />
          <Route path="/build/member/:id" element={<Protected communityMember buildLogin><RouteErrorBoundary homePath="/" homeLabel="Go to homepage"><PublicMemberProfile /></RouteErrorBoundary></Protected>} />
          <Route path="/community/collaboration/:id" element={<Protected communityMember buildLogin><RouteErrorBoundary homePath="/" homeLabel="Go to homepage"><CollaborationDeepLink /></RouteErrorBoundary></Protected>} />
          <Route path="/opportunities/:id" element={<Protected buildLogin><RouteErrorBoundary homePath="/" homeLabel="Go to homepage"><BuildOpportunityDeepLink /></RouteErrorBoundary></Protected>} />
          <Route path="/build/opportunities/:id" element={<Protected buildLogin><RouteErrorBoundary homePath="/" homeLabel="Go to homepage"><BuildOpportunityDeepLink /></RouteErrorBoundary></Protected>} />
          <Route path="/partner/me" element={<Protected buildLogin><PartnerPortalLayout /></Protected>}>
            <Route index element={<PartnerProjectsList />} />
            <Route path="discover" element={<PartnerDiscoverTalent />} />
            <Route path="projects/:id" element={<PartnerProjectDetail />} />
          </Route>
          <Route path="/client-portal/login" element={<ClientPortalLogin />} />
          <Route path="/client-portal/choose-company" element={<Protected clientPortal><ClientPortalChooseCompany /></Protected>} />
          <Route path="/client-portal" element={<Protected clientPortal><ClientPortalShell /></Protected>}>
            <Route path="home" element={<ClientPortalHome />} />
            <Route path="projects" element={<ClientPortalProjectsList />} />
            <Route path="projects/:id" element={<ClientPortalProject />} />
          </Route>
          <Route path="/partner-portal/login" element={<PartnerWorkspaceLogin />} />
          <Route path="/partner-portal/confirm-signin" element={<PartnerWorkspaceConfirmSignIn />} />
          <Route path="/partner-portal" element={<Protected partnerPortal><PartnerWorkspaceShell /></Protected>}>
            <Route path="home" element={<PartnerWorkspaceHome />} />
            <Route path="projects" element={<PartnerWorkspacePlaceholder />} />
            <Route path="discover" element={<PartnerWorkspaceDiscover />} />
            <Route path="requests" element={<PartnerWorkspacePlaceholder />} />
            <Route path="rate-card" element={<PartnerWorkspaceRateCard />} />
            <Route path="messages" element={<PartnerWorkspacePlaceholder />} />
            <Route path="profile" element={<PartnerWorkspacePlaceholder />} />
          </Route>
          <Route path="/agency/login" element={<AgencyAuth />} />
          <Route path="/agency/forgot-password" element={<AgencyAuth />} />
          <Route path="/agency/set-password" element={<AgencyAuth />} />
          <Route path="/agency/reset-password" element={<AgencyAuth />} />
          <Route path="/agency" element={<Protected agencyPortal><AgencyShell /></Protected>}>
            <Route index element={<AgencyHome />} />
            <Route path="projects" element={<AgencyProjects />} />
            <Route path="projects/:id" element={<AgencyProjectDetail />} />
            <Route path="discover" element={<AgencyDiscover />} />
            <Route path="archives" element={<AgencyArchives />} />
            <Route path="documents" element={<AgencyDocuments />} />
            <Route path="profile" element={<AgencyProfile />} />
            <Route path="estimator" element={<AgencyEstimator />} />
            <Route path="quote-requests" element={<MyQuoteRequests />} />
            <Route path="quote-requests/:id" element={<QuoteRequestDetail />} />
          </Route>
          <Route path="/recce-upload/:recceId" element={<Protected staffOnly><RecceMobileUpload /></Protected>} />
          <Route path="/projects/:id/recce" element={<Protected staffOnly><ProjectRecceFullScreen /></Protected>} />
          <Route element={<Protected staffOnly><AppShell /></Protected>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/clients" element={<ClientsList />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/partners/:id" element={<PartnerDetail />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDashboard />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/email" element={<EmailGenerator />} />
            <Route path="/commercial" element={<CommercialQuoteBuilder />} />
            <Route path="/pricing" element={<PricingManager />} />
            <Route path="/commercial-docs" element={<CommercialDocs />} />
            <Route path="/agency-quote-requests" element={<AgencyQuoteRequests />} />
            <Route path="/accounting" element={<Finance />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/build" element={<BuildDirectory />} />
            <Route path="/build/f/:id" element={<FreelancerProfile />} />
            <Route path="/admin/freelancers" element={<FreelancerManagement />} />
            <Route path="/admin/partner-rate-cards" element={<AdminPartnerRateCards />} />
            <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
            <Route path="/videos" element={<VideoLibrary />} />
            <Route path="/admin/applications" element={<ApplicationsQueue />} />
            <Route path="/admin/opportunities" element={<AdminOpportunities />} />
            <Route path="/admin/interviews" element={<InterviewQueue />} />
            <Route path="/admin/interviews/history" element={<PastInterviews />} />
            <Route path="/community" element={<Community />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/search" element={<Search />} />
            <Route path="/ai_assistant" element={<AiAssistant />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/client-requests" element={<ClientRequests />} />
            <Route path="/ledger" element={<Ledger />} />
            <Route path="/reporting" element={<Reporting />} />
            <Route path="/creative_radar" element={<CreativeRadar />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
      </>
  );
}

export default App;
